import * as Sentry from '@sentry/vue'
import { useUnleashContext } from '@unleash/proxy-client-vue'
import cookie from 'js-cookie'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { type RouteLocation } from 'vue-router'
import { useStore } from 'vuex'

import { useAuthStore } from './auth.stores'

import { config } from '@/lib/helpers'
import { useErrorHandler } from '@/lib/hooks/error.hooks'
import type { ErrorHandlingOptions } from '@/lib/types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { useAdvertisersListStore } from '@/stores/advertisers-list.stores'
import { useEntrypointStore } from '@/stores/entrypoint.stores'
import { useUserStore } from '@/stores/user.stores'

export const useApp = defineStore('app', () => {
  const updateContext = useUnleashContext()
  const legacyStore = useStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const advertiserStore = useAdvertiserStore()
  const advertisersListStore = useAdvertisersListStore()
  const entrypointStore = useEntrypointStore()
  const { handleError } = useErrorHandler()

  const isInitialized = ref(false)

  const getCampaignBoardIncludes = () =>
    userStore.isAdmin ? ['additional_payment.cost_creator'] : undefined

  const fetchCampaignAdvertiser = async (campaignId: number | string) => {
    try {
      let campaign = await legacyStore.getters['campaign/campaignInfo']

      if (!campaign || campaign?.id !== campaignId) {
        await legacyStore.dispatch('campaign/getCampaign', {
          id: campaignId,
          includes: getCampaignBoardIncludes(),
        })
        campaign = await legacyStore.getters['campaign/campaignInfo']
      }

      const advertiserId = campaign.advertiserId

      if (advertiserId !== advertisersListStore.currentId) {
        await advertiserStore.fetchAdvertiser(advertiserId)
      }
    } catch (e) {
      await legacyStore.dispatch('campaign/reset')
      await handleError({
        title: 'Campaign Loading Failed',
        message:
          'We encountered an error while loading campaign data. Please try again or contact support if the problem persists.',
        error: e,
        redirectToErrorPage: false,
      })
    }
  }

  const getHandleCampaignErrorOptions = (error): ErrorHandlingOptions => {
    switch ((error as Response)?.status) {
      case 404: {
        return {
          title: 'Campaign not found',
          message: 'The campaign you are looking for does not exist or has been removed.',
          error,
          redirectToErrorPage: false,
          showRetryButton: false,
        }
      }
      default: {
        return {
          title: 'Campaign Loading Failed',
          message:
            'We encountered an error while loading the campaign. Please try again or contact support if the problem persists.',
          error,
          redirectToErrorPage: false,
        }
      }
    }
  }

  const initialize = async (route: RouteLocation) => {
    try {
      if (!authStore.isMetapicUserCreated) {
        const isMetapicUserCreatedViaInvitation = await userStore.handleAdvertiserInvitation()
        if (isMetapicUserCreatedViaInvitation) {
          await authStore.authenticate()
        } else {
          return { name: 'onboarding' }
        }
      }
      await userStore.initialize()
      if (!userStore.user) {
        return { name: 'logout' }
      }

      if (userStore.user.advertisers.length === 0 && !userStore.user.admin) {
        return { name: 'onboarding' }
      }

      let advertiserId = userStore.user.advertisers[0]?.id

      const cookieAdvertiserId = cookie.get('advertiserId')
      if (cookieAdvertiserId) {
        advertiserId = Number(cookieAdvertiserId)
      }

      if (
        (route.name === 'campaign-users' || route.name === 'campaign-editing') &&
        route.params.id
      ) {
        await legacyStore.dispatch('campaign/getCampaign', {
          id: route.params.id,
          includes: route.name === 'campaign-users' ? getCampaignBoardIncludes() : undefined,
        })
        const campaignError = await legacyStore.getters['campaign/error']
        if (campaignError) {
          await legacyStore.dispatch('campaign/reset')
          await advertiserStore.fetchAdvertiser(advertiserId)
          return handleError(getHandleCampaignErrorOptions(campaignError))
        }
        const campaign = await legacyStore.getters['campaign/campaignInfo']

        advertiserId = campaign.advertiserId
      }

      await Promise.all([
        advertiserStore.fetchAdvertiser(advertiserId),
        entrypointStore.fetchConfig(advertiserId),
      ])
      const advertiser = advertiserStore.advertiser

      if (route.name !== 'payment' && advertiser?.stripeSubscriptionStatus === 'not_setup') {
        return { name: 'onboarding' }
      }

      if (advertiser?.stripeSubscriptionStatus === 'inactive') {
        return { name: 'edit-advertiser' }
      }

      if (userStore.user.advertisers.length === 0 && userStore.user.admin && !advertiserId) {
        return { name: 'admin-failure' }
      }
    } catch (e) {
      await handleError({
        title: 'Initialization Failed',
        message:
          'We encountered an error while setting up your account. Please try refreshing the page or contact support if the problem persists.',
        error: e,
      })
    } finally {
      isInitialized.value = true
    }
  }

  watch(
    () => userStore.user,
    async (user) => {
      if (!user) {
        return
      }

      if (config.environment === 'production') {
        Sentry.setUser({ id: String(user.id) })
      }

      if (updateContext) {
        await updateContext({
          userId: String(user.id),
          properties: {
            userEmail: user.email ?? '',
            locale: user.locale ?? '',
          },
        })
      }
    },
  )

  const terminate = () => {
    isInitialized.value = false
  }

  return {
    isInitialized,
    initialize,
    fetchCampaignAdvertiser,
    terminate,
  }
})
